import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { ReactElement, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMarketTerm } from '../../hooks/useMarketTerm';
import {
  TOTAL_SERIES,
  getDeploymentStats,
  getEquipmentBreakdownForProduct,
  getJobHistory,
  getProductUtilization,
  itemDeploymentStats,
  itemEquipmentBreakdown,
  itemEquipmentBreakdownFetching,
  itemJobHistory,
  itemProductUtilization,
  itemProductUtilizationFetching,
  jobStats,
  utilizationStats,
} from '../../redux/slices/tools/toolsSlice';
import { useDispatch } from '../../redux/store';
import { CardStyled } from '../../styled/components';
import { UtilizationChart } from '../tools/UtilizationChart';

const formatPct = (value: number): string => `${value.toFixed(value < 10 && value > 0 ? 1 : 0)}%`;

type ProductUtilizationProps = { productId: string; manufacturerId?: string };

// Shared by the two sections below so the page fetches once, not twice.
const useProductUtilization = (props: ProductUtilizationProps) => {
  const { productId, manufacturerId } = props;
  const dispatch = useDispatch();
  const breakdownFor = useSelector(getEquipmentBreakdownForProduct);
  const productSeries = useSelector(getProductUtilization);
  const jobsFor = useSelector(getJobHistory);
  const deployment = useSelector(getDeploymentStats(props.productId));
  const countsFetching = useSelector(itemEquipmentBreakdownFetching);
  const historyFetching = useSelector(itemProductUtilizationFetching);

  useEffect(() => {
    dispatch(itemEquipmentBreakdown());
    dispatch(itemJobHistory(productId));
    dispatch(itemDeploymentStats({ productId }));
    if (manufacturerId) {
      // History for this product alone; the whole organization at product grain
      // costs several times as much.
      dispatch(itemProductUtilization({ manufacturerId, productId }));
    }
  }, [dispatch, manufacturerId, productId]);

  const summary = useMemo(() => breakdownFor(productId), [breakdownFor, productId]);
  const points = productSeries[productId] || [];
  const jobs = jobsFor(productId)[TOTAL_SERIES] || [];
  return {
    summary,
    points,
    stats: utilizationStats(points),
    jobs,
    jobsStats: jobStats(jobs),
    deployment,
    countsFetching,
    historyFetching,
  };
};

// The totals for this product beside its 12-month chart, the same pairing the
// dashboard and manufacturer pages use.
export const ProductUtilizationChart = (props: ProductUtilizationProps): ReactElement => {
  const term = useMarketTerm();
  const { summary, points, stats, jobs, jobsStats, deployment, historyFetching } = useProductUtilization(props);

  const headline = [
    { label: `${term('Tracked')} now`, value: summary ? formatPct(summary.trackedPct) : '—' },
    { label: 'Max, 12 months', value: stats ? formatPct(stats.max) : '—' },
    { label: 'Avg, 12 months', value: stats ? formatPct(stats.avg) : '—' },
  ];

  // The same job-site measures the dashboard shows per category, plus how this
  // product actually gets used: how often it goes out and for how long. Two
  // columns, split where the subject changes rather than at the halfway mark.
  const jobDetail = [
    { label: 'Job sites now', value: jobs.length ? jobs[jobs.length - 1].jobCount.toLocaleString() : '—' },
    { label: 'Max job sites', value: jobsStats ? jobsStats.max.toLocaleString() : '—' },
    { label: 'Avg job sites', value: jobsStats ? jobsStats.avg.toFixed(1) : '—' },
  ];

  const stayDetail = [
    { label: 'Deployments', value: deployment ? (deployment.deployments || 0).toLocaleString() : '—' },
    { label: 'Sites reached', value: deployment ? (deployment.distinct_sites || 0).toLocaleString() : '—' },
    { label: 'Avg stay', value: deployment ? `${deployment.avg_days ?? 0} days` : '—' },
    { label: 'Longest stay', value: deployment ? `${deployment.max_days ?? 0} days` : '—' },
  ];

  return (
    <CardStyled sx={{ mb: 4, p: 6 }}>
      <Grid container spacing={6} alignItems="stretch">
        <Grid item xs={12} lg={4}>
          <Typography variant="h6">Utilization</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Share of this product at job sites
          </Typography>

          {/* The three headline percentages read across, not down: they are one
              comparison, and stacking them pushed the detail list off screen. */}
          <Stack direction="row" spacing={6} flexWrap="wrap" sx={{ mb: 1 }}>
            {headline.map((figure) => (
              <Box key={figure.label} sx={{ mb: 2 }}>
                <Typography variant="h3" component="div">
                  {figure.value}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {figure.label}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={4}>
            {[jobDetail, stayDetail].map((column, index) => (
              <Grid item xs={12} sm={6} key={index.toString()}>
                {column.map((figure) => (
                  <Stack key={figure.label} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">
                      {figure.label}
                    </Typography>
                    <Typography variant="body2" fontWeight="fontWeightBold">
                      {figure.value}
                    </Typography>
                  </Stack>
                ))}
              </Grid>
            ))}
          </Grid>

          {!!summary?.category && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>
              Product category: {summary.category}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} lg={8}>
          <UtilizationChart fetching={historyFetching} category={summary?.productName || 'Product'} points={points} />
        </Grid>
      </Grid>
    </CardStyled>
  );
};
